import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { api } from '../../../api/client';
import type { Live } from '../../../api/types';
import { Icon } from '../../../components/Icon';
import '../../Lives/Lives.css';

const EMPTY: Partial<Live> = {
    date: '',
    title: '',
    location: '',
    type: 'performance',
    sortOrder: 0,
};

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

// Parse the public Lives date string format ("2025, October 29" or
// "TBC - 2025, December 16") into a Date for comparison.
function parseLiveDate(raw: string): Date | null {
    let str = raw;
    if (str.startsWith('TBC')) {
        const parts = str.split(' - ');
        if (parts.length !== 2) return null; // pure TBC: treat as upcoming, undated
        str = parts[1];
    }
    const parts = str.trim().split(', ');
    if (parts.length !== 2) return null;
    const year = parseInt(parts[0]);
    const [month, dayStr] = parts[1].split(' ');
    const day = dayStr ? parseInt(dayStr) : 1;
    const idx = MONTH_NAMES.indexOf(month);
    if (idx === -1) return null;
    return new Date(year, idx, day);
}

// Format the raw date string the same way the public Lives page does:
// "2026, June 30"        → { date: "June 30", year: "2026" }
// "TBC - 2026, June 30"  → { date: "June - TBC", year: "2026" }
// "TBC"                  → { date: "TBC", year: "" }
function formatLiveDate(raw: string): { date: string; year: string } {
    if (raw.startsWith('TBC')) {
        const parts = raw.split(' - ');
        if (parts.length === 2) {
            const fallback = parts[1].split(', ');
            const year = fallback[0] ?? '';
            const month = fallback[1]?.split(' ')[0];
            return { date: month ? `${month} - TBC` : 'TBC', year };
        }
        return { date: 'TBC', year: '' };
    }
    const parts = raw.split(', ');
    return { date: parts[1] ?? raw, year: parts[0] ?? '' };
}

// Mirror the public Lives page: split into NEXT (upcoming or pure TBC) and
// PAST, each sorted by parsed date descending.
function categorize(rows: Live[]) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const next: Live[] = [];
    const past: Live[] = [];
    for (const row of rows) {
        const isPureTbc = row.date.startsWith('TBC') && !row.date.includes(' - ');
        const parsed = parseLiveDate(row.date);
        if (isPureTbc || !parsed) {
            next.push(row);
            continue;
        }
        (parsed >= today ? next : past).push(row);
    }
    const byDateDesc = (a: Live, b: Live) => {
        const da = parseLiveDate(a.date)?.getTime() ?? 0;
        const db = parseLiveDate(b.date)?.getTime() ?? 0;
        return db - da;
    };
    next.sort(byDateDesc);
    past.sort(byDateDesc);
    return { next, past };
}

export function LivesPanel() {
    const navigate = useNavigate();
    const location = useLocation();
    const [lives, setLives] = useState<Live[]>([]);
    const [editing, setEditing] = useState<Partial<Live> | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = () => api.lives.list().then(setLives).catch((e) => setError(e.message));

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        const m = location.pathname.match(/^\/admin\/lives\/(new|\d+)$/);
        if (!m) {
            setEditing(null);
            return;
        }
        if (m[1] === 'new') {
            setEditing(EMPTY);
            return;
        }
        const id = Number(m[1]);
        const l = lives.find((x) => x.id === id);
        if (l) setEditing(l);
    }, [location.pathname, lives]);

    const openEditor = (l: Live) => navigate(`/admin/lives/${l.id}`);
    const openNewEditor = () => navigate('/admin/lives/new');
    const closeEditor = () => navigate('/admin');

    const { next, past } = useMemo(() => categorize(lives), [lives]);

    const save = async () => {
        if (!editing) return;
        setBusy(true);
        setError(null);
        try {
            if (editing.id) {
                await api.lives.update(editing.id, editing);
            } else {
                await api.lives.create(editing);
            }
            await load();
            closeEditor();
        } catch (e: any) {
            setError(e.message ?? 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!editing?.id) return;
        if (!window.confirm(`Delete "${editing.title}"?`)) return;
        setBusy(true);
        try {
            await api.lives.remove(editing.id);
            await load();
            closeEditor();
        } catch (e: any) {
            setError(e.message ?? 'Delete failed');
        } finally {
            setBusy(false);
        }
    };

    if (editing) {
        return (
            <div className="admin-form">
                <h2>{editing.id ? 'Edit live' : 'New live'}</h2>
                <label>
                    <span>Date (e.g. "2026, March 7" or "TBC - 2026, June 30")</span>
                    <input
                        value={editing.date ?? ''}
                        onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                    />
                </label>
                <label>
                    <span>Title</span>
                    <input
                        value={editing.title ?? ''}
                        onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    />
                </label>
                <label>
                    <span>Location</span>
                    <input
                        value={editing.location ?? ''}
                        onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                    />
                </label>
                <label>
                    <span>Type</span>
                    <select
                        value={editing.type ?? 'performance'}
                        onChange={(e) =>
                            setEditing({ ...editing, type: e.target.value as Live['type'] })
                        }
                    >
                        <option value="performance">performance</option>
                        <option value="exhibition">exhibition</option>
                    </select>
                </label>
                <label>
                    <span>Sort order</span>
                    <input
                        type="number"
                        value={editing.sortOrder ?? 0}
                        onChange={(e) =>
                            setEditing({ ...editing, sortOrder: Number(e.target.value) })
                        }
                    />
                </label>
                {error && <div className="admin-error">{error}</div>}
                <div className="admin-form-actions">
                    <button className="admin-primary" onClick={save} disabled={busy}>
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={closeEditor} disabled={busy}>
                        Cancel
                    </button>
                    {editing.id && (
                        <button className="admin-danger" onClick={remove} disabled={busy}>
                            Delete
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const renderRow = (l: Live) => {
        const { date, year } = formatLiveDate(l.date);
        return (
            <div
                key={l.id}
                className="performance-item admin-perf-item"
                role="button"
                tabIndex={0}
                onClick={() => openEditor(l)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openEditor(l);
                    }
                }}
            >
                <div className="performance-date">
                    <div className="date-main">{date}</div>
                    {year && <div className="date-year">{year}</div>}
                </div>
                <div className="performance-details">
                    <span className="performance-title">"{l.title}"</span>
                    <span className="performance-location">
                        <Icon icon={FaMapMarkerAlt} className="location-icon" />
                        {l.location}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="admin-list">
            <div className="admin-list-head">
                <h2>Lives ({lives.length})</h2>
                <button onClick={openNewEditor}>+ New live</button>
            </div>
            {error && <div className="admin-error">{error}</div>}
            <div className="admin-perf-list">
                {next.length > 0 && (
                    <div className="section">
                        <div className="section-marker">NEXT</div>
                        {next.map(renderRow)}
                    </div>
                )}
                {past.length > 0 && (
                    <div className="section">
                        <div className="section-marker">PAST</div>
                        {past.map(renderRow)}
                    </div>
                )}
            </div>
        </div>
    );
}
